import jwt from 'jwt-decode';
import { RestClient } from '../restClient/index';
import { checkDomain, checkLocalhost, getAuthorizationHeader, getRedirectHref } from './utils';
import { QUERY_REDIRECT_NAME, QUERY_REDIRECT_REFRESH_TOKEN, RETRY_COUNT_502 } from './consts';
import { INextContext, INgApiConfig, TRequestMethod } from './models';
import { IResponse, IRestClient, IRestClientParams } from '../restClient/models';
import { getCookie, getTokenMinuteExpires, ICookieOptions, removeCookie, setCookie } from '../../utils/cookies';
import { ssrWrapper } from '../../utils/ssr';
import { COMMON_LINKS } from '@src/consts/links';
import { AnyType } from '@src/models/common';
import { IUser } from '@src/models/auth';
import {
  GetOptionsResponse,
  GetRequest,
  GetResponse,
  MutateRequest,
  MutateResponse,
  OptionsRequest,
  TApiEntityParam,
  UploadMediaRequest,
  UploadMediaResponse,
} from '@src/models/api';
import { getRefreshTokenName } from '../utils';
import { AuthClient } from '../authClient';
import { IAccountActionRs, ILoginRequest, TAccountError } from '../authClient/models';
import { ACCESS_TOKEN_MAX_AGE_MINUTE, REFRESH_TOKEN_MAX_AGE_MINUTE } from '@src/consts/cookies';
import { push } from '@src/utils/push';
import { SESSION_CLIENT } from '@src/utils/sessionStorageClient';
import { AccountClientManager } from '@src/utils/accountClient';
import { getAccountNotificationName } from '@src/utils/sessionStorageClient/accounts';

/**
 * Класс для работы с ng api
 *
 * !!!!Допустимая среда выполнения:!!!!!
 *  - node
 *  - браузер
 */
export class NgApiClient {
  isDevMode = false;

  /** Базовый клиент запроса */
  public restClient: IRestClient;

  /** Клиент для работы с авторизацией. */
  public authClient: AuthClient;

  /** Имя токена */
  private accessTokenName: string;

  /** Имя refresh токена */
  public refreshTokenName: string;

  /** Конфиг базовых урлов для основного клиента */
  public configUrls: {
    auth: string;
    uiBase: string;
    api: string;
    afterLogin: string;
    xapilb: string;
  };

  private clientInterceptors401;

  /** Статус для базового клиента. Т.е. будут выполняться авторизованные запросы или нет */
  private isAuthRequest = false;

  /** Метод декодирования данных о пользователе из токена */
  private decodeUser = (token: string) => {
    try {
      const user: IUser = jwt(token);
      return user || null;
    } catch (error) {
      return null;
    }
  };

  /**
   * Логика при работе с авторизацией такова, что мы всегда берем токен только из кук,
   * т.е. только там мы гарантируем что он актуален
   * (этим как раз занимается метод checkAuth в middleware у next`a или же ngApi в интерцепторах на клиенте).
   * Хранить токен в любом другом месте не безопасно и мы можем получить самые неожиданные ошибки.
   * Вызов колбека позваляет нам получить доступ до контекста запроса при проверке авторизации на сервере
   * где этот токен уже проверился в middleware и он гарантированно валидный, если есть
   */
  private serverCookieCallback: (() => INextContext) | undefined;

  constructor(config: INgApiConfig) {
    this.configUrls = {
      auth: config.authUrl,
      uiBase: config.uiBaseUrl,
      api: config.apiUrl,
      afterLogin: config.platformUrl,
      xapilb: config.xapilbUrl,
    };

    this.isDevMode = !!config.isDevMode;
    this.accessTokenName = config.cookieName;
    this.refreshTokenName = getRefreshTokenName(this.accessTokenName);

    const restClientConfig: IRestClientParams = {
      baseURL: this.configUrls.api,
    };

    this.authClient = new AuthClient({ baseUrl: this.configUrls.auth });
    this.restClient = new RestClient(restClientConfig);

    this.restClient.instance.interceptors.request.use(ssrWrapper(this.clientConfigHandler, this.serverConfigHandler));
    this.clientInterceptors401 = this.restClient.instance.interceptors.response.use(
      (response) => response,
      ssrWrapper(this.errorHandler401Client, (error) => Promise.reject(error)),
    );
    this.restClient.instance.interceptors.response.use(
      (response) => response,
      ssrWrapper(this.errorHandler502Client, (error) => Promise.reject(error)),
    );
  }

  public getAccessToken = () => {
    if (this.isAuthRequest) {
      return getCookie(this.accessTokenName);
    }
  };

  /** Настройка конфига для запроса на клиенте */
  private clientConfigHandler = (config) => {
    let token: string | undefined = undefined;

    if (this.isAuthRequest) {
      token = getCookie(this.accessTokenName);
    }

    config.headers = {
      ...config.headers,
      ...getAuthorizationHeader(token),
    };
    return config;
  };

  private serverConfigHandler = (config) => {
    if (!this.isAuthRequest) return config;

    /** Токен берем только из объекта запроса */
    const token = this.serverCookieCallback?.()?.req?.cookies?.[this.accessTokenName];

    if (token) {
      config.headers = {
        ...config.headers,
        ...getAuthorizationHeader(token),
      };
    }

    return config;
  };

  /** Обработка получения 401 ошибки при использовании авторизации */
  private errorHandler401Client = async (error: IResponse) => {
    if (error.response.status !== 401) return Promise.reject(error);

    const refreshToken = getCookie(this.refreshTokenName);

    if (!refreshToken) {
      //https://centrateam.atlassian.net/browse/CA-3048
      this.restClient.instance.interceptors.response.eject(this.clientInterceptors401);
      this.logout({ isRedirect: true });
      /**
       * В данном случае мы обязательно должны вернуть результат,
       * т.к. мы не должны запускать дальнейшую цепочку логики,
       */
      return Promise.reject(error);
    }

    try {
      const data = await this.refresh();

      if (data?.accessToken && error.config.headers) {
        error.config.headers['Authorization'] = `Bearer ${data.accessToken}`;
      }
      const result = await this.restClient.instance.request(error.config);

      return Promise.resolve(result);
    } catch (error) {
      Promise.reject(error);
    }
  };

  /** Retry при 502 */
  private errorHandler502Client = (badRequest: IResponse) => {
    return new Promise((resolve, reject) => {
      if (badRequest.response.status !== 502) return reject(badRequest);
      let retryAttempt = 0;

      if (retryAttempt <= RETRY_COUNT_502) {
        let delay = retryAttempt || 0.5;
        // По условию задержка не должна превышать 5сек
        delay = delay > 5 ? 5 : delay;
        delay *= 1000;

        retryAttempt++;

        setTimeout(async () => {
          try {
            const result = await this.restClient.instance.request(badRequest.config);
            return resolve(result);
          } catch (error) {
            return reject(badRequest);
          }
        }, delay);
      } else {
        retryAttempt = 0;
        return reject(badRequest);
      }
    });
  };

  /** При обновлении accessToken мы обязательно должны обновить restClient т.к. меняются заголовки */
  private changeToken = (accessToken?: string, cookieOptions?: ICookieOptions) => {
    if (!accessToken) return;

    const options: ICookieOptions = {
      expires: getTokenMinuteExpires(ACCESS_TOKEN_MAX_AGE_MINUTE),
      ...cookieOptions,
    };

    setCookie(this.accessTokenName, accessToken, options);
  };

  private updateAuthData = ssrWrapper(
    ({ accessToken, refreshToken }: { accessToken?: string; refreshToken?: string }) => {
      /** Доработки для localhost */
      const url = new URL(location.href);
      const cookieOptions: ICookieOptions = {};

      if (checkLocalhost(url)) {
        cookieOptions.domain = false;
      }
      /** Доработки для localhost */

      this.changeToken(accessToken, cookieOptions);

      if (refreshToken) {
        setCookie(this.refreshTokenName, refreshToken, {
          expires: getTokenMinuteExpires(REFRESH_TOKEN_MAX_AGE_MINUTE),
          ...cookieOptions,
        });
      }
    },
    () => {},
  );

  private refresh = ssrWrapper(
    async (): Promise<IAccountActionRs | null> => {
      const refreshToken = getCookie(this.refreshTokenName);
      if (!refreshToken) {
        return null;
      }

      try {
        const data = await this.authClient.authorizing(refreshToken);

        if (data) {
          if (data.error) {
            let params = '';
            const lastActiveUser = this.decodeUser(refreshToken);
            if (lastActiveUser) {
              const list = AccountClientManager.getAccountList();
              const account = list.find((acc) => acc.id === lastActiveUser.id);
              if (account) {
                params = getAccountNotificationName(account);
              }
            }

            SESSION_CLIENT.setNotification({
              data: data.error?.reason || 'COMMON_ERROR',
              params,
            });

            this.toMainAuthPage();
          }

          this.updateAuthData(data);
          return data;
        } else {
          this.isAuthRequest && this.logout({ isRedirect: true });
          return null;
        }
      } catch {
        this.isAuthRequest && this.logout({ isRedirect: true });
        return null;
      }
    },
    async () => {
      // Сейчас обработка добавлена в next бандлах
    },
  );

  /**
   * Функция для logout для очистки состояния авторизации
   * @param isRedirect bool - сохранять/не сохранять параметр rc после выхода
   * @param isDemoExit bool - логаут из деморежима, редиректит на лендинг
   */
  public logout = ({ isRedirect, isDemoExit }: { isRedirect: boolean; isDemoExit?: boolean }) => {
    const fromUrl = new URL(location.href);
    const cookieOptions: ICookieOptions = {};

    if (checkLocalhost(fromUrl)) {
      cookieOptions.domain = false;
    }

    removeCookie(this.accessTokenName, cookieOptions);
    removeCookie(this.refreshTokenName, cookieOptions);

    const url = new URL(`${this.configUrls.uiBase}${COMMON_LINKS.LOGIN.href}`);
    const demoExitUrl = new URL(`${this.configUrls.uiBase}/?rtd=1`);
    if (isRedirect) {
      url.searchParams.set(QUERY_REDIRECT_NAME, fromUrl.href);
    }

    push(isDemoExit ? demoExitUrl.href : url.href);
  };

  public redirect = ssrWrapper(
    (tokenData: { accessToken: string; refreshToken: string }) => {
      const url = new URL(window.location.href);
      const redirectHref = getRedirectHref(url, this.configUrls);

      /** Здесь простая проверка на перенаправление именно на домены centra */
      if (redirectHref) {
        const redirectUrl = new URL(redirectHref);
        if (checkDomain(redirectUrl) || checkLocalhost(redirectUrl)) {
          /** Для работы с localhost, кидаем токен через query параметр для получения после редиректа */
          if (checkLocalhost(redirectUrl) && tokenData.refreshToken) {
            redirectUrl.searchParams.set(QUERY_REDIRECT_REFRESH_TOKEN, tokenData.refreshToken);
          }
          /** Для работы с localhost */

          push(redirectUrl.href);
          return;
        }
      }

      this.logout({ isRedirect: false });
    },
    () => {},
  );

  private getUrl = (entityId: TApiEntityParam<string>, method: TRequestMethod) => `/ng/v1/${entityId}/${method}`;

  /**
   * Не всегда инфо юзера нужен для авторизации или показа данных юзеру.
   * Этот метод используется еще и в аналитике, чтобы понять кто залогинен и
   * при этом юзер может находится в публичной зоне, поэтому ВАЖНО, чтобы
   * не стояло проверок по isAuthRequest или чему-то подобном.
   *
   * Также ВАЖНО, чтобы тут не было моментов рефреша токена, так как если это аналитика,
   * то хорошо, если мы будем знать юзера в любом случае.
   * Если он сделает разлогин, то токена не будет и аналитики не будет.
   *
   * В приватной зоне токен будет обновлен
   */
  public getUserInfoFromAccessToken = (): IUser | null => {
    const accessToken = getCookie(this.accessTokenName);

    if (accessToken) {
      const user = this.decodeUser(accessToken);
      if (user) return user;
    }

    return null;
  };

  /** Получить текущего авторизованного юзера */
  public getUser = async () => {
    if (!this.isAuthRequest) return null;

    /** Обрабатываем случай локальной сборки когда refresToken передается по урлу */
    const url = new URL(location.href);
    const refreshToken = url.searchParams.get(QUERY_REDIRECT_REFRESH_TOKEN);

    const user = this.getUserInfoFromAccessToken();
    if (user) {
      /** Удаляем  refreshToken если он есть в урле, т.е. если есть есть access то и refresh в куках тоже есть */
      if (refreshToken) {
        /** После того как считали и записали токен в куки лучше его очистить из url */
        url.searchParams.delete(QUERY_REDIRECT_REFRESH_TOKEN);
        push(url.href);
      }

      return user;
    }

    if (refreshToken) {
      setCookie(this.refreshTokenName, refreshToken, {
        domain: false,
        expires: getTokenMinuteExpires(REFRESH_TOKEN_MAX_AGE_MINUTE),
      });

      const authData = await this.refresh();
      if (authData && authData?.accessToken) {
        // Это тот случай когда мы передает refreshToken через урл при локальной запуске, здесь домен не нужен
        this.changeToken(authData.accessToken, {
          domain: false,
        });
      }

      /** После того как считали и записали токен в куки лучше его очистить из url */
      url.searchParams.delete(QUERY_REDIRECT_REFRESH_TOKEN);
      push(url.href);

      return authData?.accessToken ? this.decodeUser(authData.accessToken) : null;
    }

    const data = await this.refresh();
    return data?.accessToken ? this.decodeUser(data.accessToken) : null;
  };

  /**
   * Метод для получения данных.
   *
   * @param {TApiEntity} entityId значение сущности.
   * @param {GetRequest} request объект запроса соответствующий схеме апи.
   */
  public get<Rq = AnyType, TEntity = unknown>(entityId: TApiEntityParam<TEntity>, request: GetRequest) {
    return this.restClient.post<GetRequest, GetResponse<Rq>>(this.getUrl(entityId, 'get'), request);
  }

  /**
   * Метод для мутации данных (добавления/изменения/удаления).
   *
   * @param {TApiEntity} entityId значение сущности.
   * @param {MutateRequest} request объект запроса соответствующий схеме апи.
   */
  public mutate<TEntity = unknown>(entityId: TApiEntityParam<TEntity>, request: MutateRequest) {
    return this.restClient.post<MutateRequest, MutateResponse>(this.getUrl(entityId, 'mutate'), request);
  }

  /**
   * Метод для получения данных для опшенов
   */
  public options<TEntity = unknown>(
    entityId: TApiEntityParam<TEntity>,
    optionListId: string,
    request?: OptionsRequest,
  ) {
    return this.restClient.post<OptionsRequest, GetOptionsResponse>(
      this.getUrl(entityId, 'options') + '/' + optionListId,
      request,
    );
  }

  /**
   * Метод для загрузки изображений стандартным методом для ng api
   *
   * Например:
   * @link https://api.dev.centra.ai/docs/#/banner/uploadImageFile
   */
  public uploadMedia<TEntity = unknown>(
    entityId: TApiEntityParam<TEntity>,
    entityFieldId: string,
    request?: UploadMediaRequest,
  ) {
    return this.restClient.upload<UploadMediaRequest, UploadMediaResponse>(
      this.getUrl(entityId, 'uploadMedia') + '/' + entityFieldId,
      request,
    );
  }

  /** Выполняет процесс авторизации и редиректит в успешном случае */
  public async login(requestData: ILoginRequest, isAddAccount?: boolean) {
    let result = await this.authClient.login(requestData);

    if (isAddAccount) {
      const refreshToken = getCookie(this.refreshTokenName) || '';
      result = await this.authClient.addAccount(refreshToken, result.refreshToken);
    }

    if (result) {
      this.updateAuthData(result);
      this.redirect(result);
    }

    return result;
  }

  /** Переход на страницу логина
   * @param query любые query параметры используемые для логина
   * @param isSaveRedirect стоит ли сохранять текущий урл для последующего редиректа после успешной авторизации
   */
  public toLogin({ isSaveRedirect = true, query }: { query?: { [x: string]: string }; isSaveRedirect?: boolean } = {}) {
    const url = new URL(`${this.configUrls.uiBase}${COMMON_LINKS.LOGIN.href}`);

    if (isSaveRedirect || this.isDevMode) {
      url.searchParams.set(QUERY_REDIRECT_NAME, location.href);
    }

    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        url.searchParams.set(key, value);
      });
    }

    push(url.href);
  }

  /** Переход на главную авторизованной зоны */
  public toMainAuthPage = () => {
    push(this.configUrls.afterLogin);
  };

  /** Метод управления авторизацией для запросов */
  public setIsAuthRequest = (isAuth: boolean) => {
    this.isAuthRequest = isAuth;
  };

  public setServerCookieCallback = (cb: () => INextContext) => {
    this.serverCookieCallback = cb;
  };

  /** Переходим на страницу добавления аккаунта */
  /** @param email - подставляем почту в случае добавления аккаунта из которого вышли */
  public toAddAccount(email?: string) {
    const query: { [key: string]: string } = { u: '0' };

    if (typeof email === 'string') {
      query.e = email;
    }

    this.toLogin({ isSaveRedirect: false, query });
  }

  public async changeAccount(id: number) {
    try {
      const rt = getCookie(this.refreshTokenName);

      if (!rt) return { result: false };

      const result = await this.authClient.changeAccount(rt, id);

      if (result.error) {
        return {
          result: false,
          error: result.error,
        };
      } else {
        this.updateAuthData(result);
        return { result: true };
      }
    } catch (e) {
      const error = (e as { response: { data: TAccountError } })?.response?.data;
      return {
        result: false,
        error,
      };
    }
  }

  public async deleteAccount(id: number) {
    try {
      const rt = getCookie(this.refreshTokenName);
      if (!rt) return false;

      const result = await this.authClient.deleteAccount(rt, id);
      this.updateAuthData(result);
      return true;
    } catch (error) {
      return false;
    }
  }
}
